import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import UndoStripMolecule from '../UndoStrip/UndoStripMolecule';
import type { UseFactorRosterResult } from './useFactorRoster';
import type { RosterRemoval } from './cells/ActionsCell';

const REASON_MAX_LENGTH = 280;
const REASON_COUNTER_FROM = 240;

type StripError = 'restore' | 'reason' | 'gone';

export interface RollupRosterRemovalStripProps {
  removal: RosterRemoval;
  /** Reverses the removal and refetches the roster. */
  undo: () => Promise<void>;
  setExclusionReason: UseFactorRosterResult['setExclusionReason'];
  /** Closes the strip; the molecule returns focus to the roster heading. */
  onClose: () => void;
}

/**
 * The roster's "a member was just removed" strip: Undo for either removal and, for an exclusion, an
 * inline reason field. Lives in the molecule (not the row) because the refetch unmounts the row.
 * Mount it with a `key` per removal so a new removal starts from a clean state.
 */
const RollupRosterRemovalStrip: React.FC<RollupRosterRemovalStripProps> = ({
  removal,
  undo,
  setExclusionReason,
  onClose,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<StripError | null>(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonText, setReasonText] = useState('');
  const [reasonSaved, setReasonSaved] = useState(false);
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const addReasonRef = useRef<HTMLSpanElement>(null);
  const returnFocusToAdd = useRef(false);

  useEffect(() => {
    if (reasonOpen) {
      inputRef.current?.focus();
    } else if (returnFocusToAdd.current) {
      returnFocusToAdd.current = false;
      addReasonRef.current?.querySelector('button')?.focus();
    }
  }, [reasonOpen]);

  const runUndo = async () => {
    setBusy(true);
    setError(null);
    try {
      await undo();
      onClose();
    } catch {
      setError('restore');
      setBusy(false);
    }
  };

  const saveReason = async () => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await setExclusionReason(removal.sphereId, reasonText.trim());
      if (outcome === 'gone') {
        setError('gone');
      } else {
        setReasonSaved(true);
        setReasonOpen(false);
      }
    } catch {
      setError('reason');
    } finally {
      setBusy(false);
    }
  };

  const collapseReason = () => {
    returnFocusToAdd.current = true;
    setReasonText('');
    setReasonOpen(false);
  };

  const name = removal.memberName;
  let message: string;
  if (error === 'restore') {
    message = t(
      'statusFactors.roster.undoStrip.restoreFailed',
      "Couldn't restore {{name}} — the change didn't save, so it is still removed. Try again.",
      { name }
    );
  } else if (error === 'reason') {
    message = t('statusFactors.roster.undoStrip.reasonFailed', "Couldn't save the reason — try again.");
  } else if (error === 'gone') {
    message = t('statusFactors.roster.undoStrip.reasonGone', 'This exclusion was already lifted.');
  } else if (reasonSaved) {
    message = t('statusFactors.roster.undoStrip.reasonSaved', 'Reason saved');
  } else if (removal.kind === 'excluded') {
    message = t('statusFactors.roster.undoStrip.excluded', '{{name}} excluded', { name });
  } else {
    message = t('statusFactors.roster.unlinked', '{{name}} unlinked', { name });
  }

  let extra: React.ReactNode = null;
  if (removal.kind === 'excluded' && !reasonSaved && !error) {
    extra = reasonOpen ? (
      <div className="rollup-roster__reason" data-testid="roster-reason-field">
        <label htmlFor={inputId} className="rollup-roster__reason-label">
          {t('statusFactors.roster.undoStrip.reasonLabel', 'Reason')}
        </label>
        <div className="rollup-roster__reason-control">
          <InputAtom
            id={inputId}
            ref={inputRef}
            className="rollup-roster__reason-input"
            value={reasonText}
            maxLength={REASON_MAX_LENGTH}
            disabled={busy}
            placeholder={t('statusFactors.roster.undoStrip.reasonPlaceholder', 'Why is it excluded?')}
            onChange={(event) => setReasonText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void saveReason();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                collapseReason();
              }
            }}
          />
          {reasonText.length >= REASON_COUNTER_FROM && (
            <span className="rollup-roster__reason-count">
              {reasonText.length}/{REASON_MAX_LENGTH}
            </span>
          )}
          <ButtonAtom
            variant="text"
            size="small"
            label={t('statusFactors.roster.undoStrip.reasonSave', 'Save')}
            disabled={busy}
            onClick={() => void saveReason()}
          />
        </div>
      </div>
    ) : (
      <span ref={addReasonRef}>
        <ButtonAtom
          variant="text"
          size="small"
          label={t('statusFactors.roster.undoStrip.addReason', 'Add a reason')}
          onClick={() => setReasonOpen(true)}
        />
      </span>
    );
  }

  let retryHandler: (() => void) | undefined;
  if (error === 'restore') retryHandler = () => void runUndo();
  else if (error === 'reason') retryHandler = () => void saveReason();

  return (
    <UndoStripMolecule
      className="rollup-roster__strip"
      message={message}
      variant={error ? 'error' : 'default'}
      extra={extra}
      undoLabel={t('statusFactors.roster.undo', 'Undo')}
      dismissLabel={t('statusFactors.roster.undoStrip.dismiss', 'Dismiss')}
      retryLabel={t('statusFactors.roster.retry', 'Retry')}
      onUndo={() => void runUndo()}
      onDismiss={onClose}
      onRetry={retryHandler}
      busy={busy}
      focusKey={`${error ?? 'ok'}:${reasonSaved}`}
    />
  );
};

export default RollupRosterRemovalStrip;
